import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useLayoutEffect, useState, } from 'react';
import { Handle, Position, useConnection, useStore, useUpdateNodeInternals, } from '@xyflow/react';
import { ArrowRight, ChevronsRight, GripVertical, Plus, Trash2, } from 'lucide-react';
import { DRAFT_HANDLE_PREFIX, ENTRY_HANDLE_ID, EXIT_HANDLE_ID, FlowMetaContext, sourceSlotFor, } from '../FlowEditorContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '../components/ui/tooltip';
import { listOrder } from '../arrow-order';
import { exitInsetFromRight } from '../line-geometry';
import { EDGE_STROKE_WIDTH, HIGHLIGHT_OUTLINE } from '../editor-styles';
import { cn } from '../lib/utils';
import { InsertMenu } from './InsertMenu';
// Card chrome the rows sit inside: `px-3 py-2.5` plus a 2px border. A row's
// line stub spans the right inset from the row to the card's edge, and the
// layout reads the bottom inset to find each row's port.
export const CARD_RIGHT_INSET = 14;
// A step card's `w-[220px]`, for before React Flow has measured the card.
export const STEP_CARD_WIDTH = 220;
export const CARD_BOTTOM_INSET = 12;
// `h-7` rows, `space-y-1` between them.
export const TRANSITION_ROW_HEIGHT = 28;
export const TRANSITION_ROW_PITCH = 32;
// `mt-2.5` (10), the 1px divider, `pt-2` (8) and the `h-6` (24) "Transitions"
// header. The `mt-1` above the first row counts in its pitch.
const TRANSITION_HEADER_HEIGHT = 43;
// Center of the header row: the 2px border, `py-2.5`, half the 20px line. In
// TB the entry dot sits on the top edge, the same distance from the left.
export const ENTRY_HANDLE_INSET = 22;
// A dot that needs attention: an entry no path reaches, or a draft's exit.
const AMBER_DOT_CLASS = '!h-2.5 !w-2.5 !border-2 !border-background !bg-amber-500';
/** A card's one entry dot: left edge by the header in LR, top edge in TB. */
export function EntryHandle({ nodeId }) {
    const { reachable, direction } = useContext(FlowMetaContext);
    const top = direction === 'TB';
    // React Flow caches where each handle sits. Moving the dot to the other
    // edge must refresh that, or arrows keep landing on the old spot.
    const updateNodeInternals = useUpdateNodeInternals();
    useLayoutEffect(() => {
        updateNodeInternals(nodeId);
    }, [nodeId, top, updateNodeInternals]);
    // A boolean, so the dot redraws when it opens or closes, not on every move.
    const open = useConnection(connection => connection.inProgress && connection.fromNode?.id !== nodeId);
    return (_jsx(Handle, { type: "target", position: top ? Position.Top : Position.Left, id: ENTRY_HANDLE_ID, className: cn('!border-0', open
            ? '!h-3 !w-3 !bg-[color:var(--edge-highlight)]'
            : reachable.has(nodeId)
                ? '!h-2 !w-2 !bg-muted-foreground/70'
                : AMBER_DOT_CLASS), style: {
            ...(top ? { left: ENTRY_HANDLE_INSET } : { top: ENTRY_HANDLE_INSET }),
            // Size and color only: animating the position would leave React Flow
            // measuring the dot mid-move, at its old edge, after a layout switch.
            transition: 'width 150ms, height 150ms, background-color 150ms',
        } }));
}
const UNCONDITIONAL = 'Unconditional';
// The line of an arrow that never fires: Tailwind's amber-500.
export const BLOCKED_STROKE = '#f59e0b';
/** How much the transition list adds to a card with `count` rows. */
export function transitionListHeight(count) {
    return TRANSITION_HEADER_HEIGHT + count * TRANSITION_ROW_PITCH;
}
/**
 * A card's transitions, in the order they are checked; each arrow leaves
 * from its row's dot. Drafts sit among them until connected. A say or
 * end-conversation card is `unconditional`: one fixed, unselectable row.
 * Start is `start`: its pre-greeting arrows and drafts, then its arrow with
 * no condition, fixed at the bottom.
 */
export function TransitionList({ nodeId, unconditional = false, start = false, conditionOptional = false, hovered = false, }) {
    const { transitionsBySource, draftsBySource, edgeLabels, direction, selectArrow, openTransitions, deleteArrow, moveListRow, insertKinds, appendAfter, blockedArrows, addDraft, selectDraft, removeDraft, } = useContext(FlowMetaContext);
    const updateNodeInternals = useUpdateNodeInternals();
    const rows = transitionsBySource.get(nodeId) ?? [];
    const drafts = unconditional ? [] : (draftsBySource.get(nodeId) ?? []);
    // A fixed row with no arrow yet: an exit to drag to a node.
    const exitRow = (unconditional && rows.length === 0) ||
        (start && !rows.some(row => row.fallback));
    const exitLabel = start && rows.length > 0 ? 'All other calls' : UNCONDITIONAL;
    // Arrows are drawn under the cards, so a connected row draws the stretch
    // from its dot to the card's edge itself.
    const bottom = direction === 'TB';
    // The rows span the card less its side insets; a step card and a form card
    // differ in width.
    const rowWidth = (useStore(state => state.nodeLookup.get(nodeId)?.measured.width) ??
        STEP_CARD_WIDTH) -
        2 * CARD_RIGHT_INSET;
    const rowExitId = (target) => sourceSlotFor({ source: nodeId, target, side: 'right' });
    // Arrows and drafts in one list, each draft at its own place among the
    // arrows. The list order is what a drag changes.
    const listed = listOrder(rows, drafts);
    const exits = [
        ...listed.map(ref => ref.draft
            ? { id: `${DRAFT_HANDLE_PREFIX}${ref.id}`, connected: false }
            : { id: rowExitId(ref.row.target), connected: true }),
        ...(exitRow ? [{ id: EXIT_HANDLE_ID, connected: false }] : []),
    ];
    // Top to bottom, a row's line runs down past the rows below it. Each lower
    // row's dot sits a step further left, so no line crosses a dot and the
    // lines leave the card at separate points.
    const placeOf = (id) => {
        const index = exits.findIndex(exit => exit.id === id);
        return {
            connected: exits[index]?.connected ?? true,
            fromRight: exitInsetFromRight(index, rowWidth),
            // Row bottom to the card's outer bottom edge.
            drop: (exits.length - 1 - index) * TRANSITION_ROW_PITCH + CARD_BOTTOM_INSET,
        };
    };
    // A dot that is not connected has no arrow yet: amber, and it can be
    // dragged to a node.
    const exitDot = (id) => {
        const { connected, fromRight } = placeOf(id);
        return (_jsx(Handle, { type: "source", position: bottom ? Position.Bottom : Position.Right, id: id, isConnectable: !connected, className: connected
                ? '!h-2 !w-2 !border-0 !bg-muted-foreground/70'
                : AMBER_DOT_CLASS, style: bottom ? { left: `calc(100% - ${fromRight}px)` } : undefined }, id));
    };
    // Green on the same rule as the arrow: the transition, its card or the card
    // it leads to is selected.
    const selectedNodeIds = useStore(state => state.nodes
        .filter(node => node.selected)
        .map(node => node.id)
        .join(','));
    const lit = (row) => row.selected ||
        selectedNodeIds.split(',').some(id => id === nodeId || id === row.target);
    const exitStub = (id, tone) => {
        const { fromRight, drop } = placeOf(id);
        const color = tone === 'green'
            ? 'var(--edge-highlight)'
            : tone === 'amber'
                ? BLOCKED_STROKE
                : 'var(--xy-edge-stroke, #b1b1b7)';
        return (_jsx("span", { "aria-hidden": true, className: "pointer-events-none absolute z-10", style: bottom
                ? {
                    left: `calc(100% - ${fromRight}px)`,
                    top: '100%',
                    width: EDGE_STROKE_WIDTH,
                    height: drop,
                    transform: 'translateX(-50%)',
                    background: color,
                }
                : {
                    left: '100%',
                    top: '50%',
                    width: CARD_RIGHT_INSET,
                    height: EDGE_STROKE_WIDTH,
                    transform: 'translateY(-50%)',
                    background: color,
                } }));
    };
    const fingerprint = [direction, ...exits.map(exit => exit.id)].join(',');
    useLayoutEffect(() => {
        updateNodeInternals(nodeId);
    }, [nodeId, fingerprint, updateNodeInternals]);
    // A row is draggable only while its grip is held, so a click on the row
    // still selects it. `dropAt` is the place the dragged row would land.
    const [armedId, setArmedId] = useState(null);
    const [draggingId, setDraggingId] = useState(null);
    const [dropAt, setDropAt] = useState(null);
    const endDrag = () => {
        setArmedId(null);
        setDraggingId(null);
        setDropAt(null);
    };
    // What a draggable row needs: the drag itself, and where it would land.
    const dragProps = (id, index) => ({
        draggable: armedId === id,
        onDragStart: (event) => {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', id);
            setDraggingId(id);
        },
        onDragOver: (event) => {
            if (!draggingId)
                return;
            event.preventDefault();
            setDropAt(index);
        },
        onDrop: (event) => {
            event.preventDefault();
            if (draggingId && dropAt !== null)
                moveListRow(nodeId, draggingId, dropAt);
            endDrag();
        },
        onDragEnd: endDrag,
    });
    const dropMarker = (id, index) => dropAt === index &&
        draggingId &&
        draggingId !== id && (_jsx("span", { "aria-hidden": true, className: "absolute inset-x-1 h-0.5 rounded bg-primary", style: listed.findIndex(ref => ref.id === draggingId) < index
            ? { bottom: -3 }
            : { top: -3 } }));
    // The grip that arms a row for dragging. On hover it replaces the icon.
    const grip = (id, icon) => (_jsxs("span", { className: "flex h-5 w-5 flex-shrink-0 cursor-grab items-center justify-center", onPointerDown: () => {
            setArmedId(id);
            // A release off the grip disarms the row too.
            window.addEventListener('pointerup', () => setArmedId(null), {
                once: true,
            });
        }, children: [_jsx("span", { className: "group-hover:hidden", children: icon }), _jsx(GripVertical, { "aria-label": "Drag to reorder", className: "hidden h-3.5 w-3.5 text-muted-foreground group-hover:block" })] }));
    return (_jsxs("div", { className: "mt-2.5 border-t border-border pt-2", children: [_jsxs("div", { className: "relative flex h-6 items-center", children: [_jsx("button", { type: "button", className: "nodrag nopan flex h-full flex-1 items-center text-left text-xs font-medium text-muted-foreground hover:text-foreground", onClick: event => {
                            event.stopPropagation();
                            openTransitions(nodeId);
                        }, children: unconditional ? 'Transition' : 'Transitions' }), !unconditional && (_jsx("button", { type: "button", "aria-label": "Add transition", title: "Add transition", className: "nodrag nopan ml-auto flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground", onClick: event => {
                            event.stopPropagation();
                            addDraft(nodeId);
                        }, children: _jsx(Plus, { className: "h-3.5 w-3.5" }) }))] }), rows.length === 0 && (
            // A card with no way out gets a "+" on its exit edge.
            _jsx("div", { className: "absolute z-10", style: bottom
                    ? { left: '50%', top: '100%', transform: 'translate(-50%, -50%)' }
                    : { left: '100%', top: '50%', transform: 'translate(-50%, -50%)' }, children: _jsx(InsertMenu, { kinds: insertKinds(nodeId), visible: hovered, label: "Add a node after this one", onChoose: kind => appendAfter(nodeId, kind) }) })), (exitRow || rows.length + drafts.length > 0) && (_jsx(TooltipProvider, { children: _jsxs("ul", { className: "mt-1 space-y-1", children: [listed.map((ref, index) => {
                            if (ref.draft) {
                                const draft = ref.row;
                                const arrowIcon = (_jsx(ArrowRight, { className: "h-3 w-3 text-amber-600 dark:text-amber-500" }));
                                return (_jsx(NotConnectedTip, { children: _jsxs("li", { ...(draft.label ? dragProps(draft.id, index) : {}), onDragOver: dragProps(draft.id, index).onDragOver, onDrop: dragProps(draft.id, index).onDrop, className: cn('nodrag nopan group relative flex h-7 cursor-pointer items-center gap-1 rounded-md border border-dashed bg-background pl-1 pr-1 text-xs', 'border-amber-500/60 text-foreground', draggingId === draft.id && 'opacity-40'), style: draft.selected ? HIGHLIGHT_OUTLINE : undefined, onClick: event => {
                                            event.stopPropagation();
                                            selectDraft(draft.id);
                                        }, children: [dropMarker(draft.id, index), draft.label ? (grip(draft.id, arrowIcon)) : (_jsx("span", { className: "flex h-5 w-5 flex-shrink-0 items-center justify-center", children: arrowIcon })), _jsx("span", { className: cn('min-w-0 flex-1 truncate', !draft.label && 'italic text-muted-foreground'), children: draft.label || 'Set a condition' }), _jsx(DeleteButton, { label: "Remove draft transition", onDelete: () => removeDraft(draft.id) }), exitDot(`${DRAFT_HANDLE_PREFIX}${draft.id}`)] }) }, draft.id));
                            }
                            const row = ref.row;
                            const label = unconditional
                                ? UNCONDITIONAL
                                : edgeLabels.get(row.id) ||
                                    (conditionOptional ? UNCONDITIONAL : '');
                            const selected = !unconditional && row.selected;
                            // Start's fallback is checked last, so it keeps its place.
                            const fixed = unconditional || !!row.fallback;
                            // A transition with no condition where one is needed never
                            // fires.
                            const blocked = blockedArrows.has(row.id);
                            const iconColor = blocked
                                ? 'text-amber-600 dark:text-amber-500'
                                : 'text-primary';
                            const arrowIcon = row.preGreeting ? (_jsx(ChevronsRight, { "aria-label": "Pre-greeting", className: cn('h-3.5 w-3.5', iconColor) })) : (_jsx(ArrowRight, { className: cn('h-3 w-3', iconColor) }));
                            return (_jsxs("li", { ...(fixed ? {} : dragProps(row.id, index)), className: cn('nodrag nopan group relative flex h-7 items-center gap-1 rounded-md border bg-background pl-1 pr-1 text-xs text-foreground', !unconditional && 'cursor-pointer', blocked
                                    ? 'border-dashed border-amber-500/60'
                                    : 'border-border', draggingId === row.id && 'opacity-40'), style: selected ? HIGHLIGHT_OUTLINE : undefined, title: label || 'Set a condition', onClick: event => {
                                    event.stopPropagation();
                                    if (!unconditional)
                                        selectArrow(row.id);
                                }, children: [dropMarker(row.id, index), fixed ? (_jsx("span", { className: "flex h-5 w-5 flex-shrink-0 items-center justify-center", children: arrowIcon })) : (grip(row.id, arrowIcon)), _jsx("span", { className: cn('min-w-0 flex-1 truncate', !label && 'italic text-muted-foreground'), children: label || 'Set a condition' }), !fixed && (_jsx(DeleteButton, { label: "Delete transition", onDelete: () => deleteArrow(row.id) })), exitStub(rowExitId(row.target), lit(row) ? 'green' : blocked ? 'amber' : 'gray'), exitDot(rowExitId(row.target))] }, row.id));
                        }), exitRow && (_jsx(NotConnectedTip, { children: _jsxs("li", { className: "nodrag nopan relative flex h-7 items-center gap-1 rounded-md border border-dashed border-amber-500/60 bg-background pl-1 pr-1 text-xs", onClick: event => event.stopPropagation(), children: [_jsx("span", { className: "flex h-5 w-5 flex-shrink-0 items-center justify-center", children: _jsx(ArrowRight, { className: "h-3 w-3 text-amber-600 dark:text-amber-500" }) }), _jsx("span", { className: "min-w-0 flex-1 truncate", children: exitLabel }), exitDot(EXIT_HANDLE_ID)] }) }))] }) }))] }));
}
/** Marks a card a pre-greeting arrow leads to. */
export function PreGreetingMark({ nodeId }) {
    const { transitionsBySource } = useContext(FlowMetaContext);
    const marked = [...transitionsBySource.values()].some(rows => rows.some(row => row.preGreeting && row.target === nodeId));
    if (!marked)
        return null;
    return (_jsx("span", { title: "A pre-greeting route leads here", className: "ml-auto flex", children: _jsx(ChevronsRight, { "aria-label": "Pre-greeting route", className: "h-3.5 w-3.5 flex-shrink-0 text-primary" }) }));
}
/** A card that is not reachable shows a dashed amber border; hovering
 *  spells it out. A reachable card renders as it is. */
export function NodeUnreachableTip({ nodeId, children, }) {
    const { reachable, waitingOnCondition } = useContext(FlowMetaContext);
    if (reachable.has(nodeId))
        return children;
    return (_jsx(TooltipProvider, { children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: children }), _jsx(TooltipContent, { side: "top", children: waitingOnCondition.has(nodeId)
                        ? 'Node not reachable: a transition into it has no condition'
                        : 'Node not reachable in this graph' })] }) }));
}
/** A row that is not connected shows a dashed amber border; hovering spells
 *  it out. */
function NotConnectedTip({ children }) {
    return (_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: children }), _jsx(TooltipContent, { side: "top", children: "Transition not connected" })] }));
}
/** The delete button a row shows on hover. Clicking the row edits it. */
function DeleteButton({ label, onDelete, }) {
    return (_jsx("button", { type: "button", "aria-label": label, title: label, 
        // The gap on the right keeps it clear of the row's exit dot.
        className: "mr-1.5 hidden h-5 w-5 flex-shrink-0 items-center justify-center rounded text-destructive hover:bg-muted group-hover:flex", onClick: event => {
            event.stopPropagation();
            onDelete();
        }, children: _jsx(Trash2, { className: "h-3 w-3" }) }));
}
//# sourceMappingURL=TransitionList.js.map